import React from "react";
import Box from "@mui/joy/Box";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import IconButton from "@mui/joy/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
// Removed accordion components to match other pages' table structure
import { gql, useMutation, useQuery } from "@apollo/client";

const REWARD_SETTINGS_QUERY = gql`
  query RewardSettings {
    rewardSettings {
      id
      reason
      points
    }
  }
`;

// no static items; data comes from rewardSettings

export default function Rewards() {
  const { data, refetch } = useQuery(REWARD_SETTINGS_QUERY, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const settings = data?.rewardSettings || [];
  const dynamicItems = settings.map((s: any) => ({
    id: s.id,
    name: s.reason
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^.|\s./g, (m: string) => m.toUpperCase()),
    value: s.points,
  }));

  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set(["register_profile"]) // default expanded section
  );

  const [isEditing, setIsEditing] = React.useState(false);
  const [editedValues, setEditedValues] = React.useState<
    Record<string, number>
  >({});
  const [saving, setSaving] = React.useState(false);

  const [mutate] = useMutation(gql`
    mutation UpdateRewardSetting($id: ID!, $points: Int!) {
      updateRewardSetting(input: { id: $id, points: $points }) {
        rewardSetting {
          id
          points
        }
      }
    }
  `);

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography level="h2" sx={{ mb: 1 }}>
        Rewards
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
        {isEditing ? (
          <>
            <IconButton
              aria-label="cancel edit"
              variant="soft"
              color="danger"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setEditedValues({});
              }}
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              aria-label="save changes"
              variant="soft"
              color="success"
              size="sm"
              onClick={async () => {
                setSaving(true);
                try {
                  const changes = Object.entries(editedValues);
                  if (changes.length > 0) {
                    await Promise.all(
                      changes.map(([id, points]) =>
                        mutate({
                          variables: { id, points: Math.round(Number(points)) },
                        })
                      )
                    );
                    await refetch();
                  }
                } finally {
                  setSaving(false);
                  setIsEditing(false);
                  setEditedValues({});
                }
              }}
              disabled={saving}
            >
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            aria-label="edit rewards"
            variant="outlined"
            color="primary"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <EditIcon />
          </IconButton>
        )}
      </Box>

      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "hidden",
        }}
      >
        <Table
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "6px",
            "--TableCell-paddingX": "12px",
          }}
        >
          <tbody>
            {/* Register & Profile */}
            <tr style={{ background: "var(--joy-palette-background-level1)" }}>
              <td style={{ padding: "8px 6px" }}>
                <IconButton
                  aria-label="expand row"
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => toggleRow("register_profile")}
                  sx={{ mr: 0.5 }}
                >
                  {expandedRows.has("register_profile") ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: 600, display: "inline" }}
                >
                  Register & Profile
                </Typography>
              </td>
              <td></td>
            </tr>
            {expandedRows.has("register_profile") &&
              dynamicItems.map((item: any, iIdx: number) => (
                <tr key={iIdx}>
                  <td style={{ padding: "12px 20px" }}>
                    <Typography level="body-sm">{item.name}</Typography>
                  </td>
                  <td
                    style={{
                      padding: "12px 6px",
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <input
                      disabled={!isEditing}
                      value={(editedValues[item.id] ?? item.value).toString()}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^0-9]/g, "");
                        setEditedValues((prev) => ({
                          ...prev,
                          [item.id]: Number(next || 0),
                        }));
                      }}
                      style={{
                        width: "clamp(56px, 7vw, 88px)",
                        textAlign: "center",
                        padding: "2px 20px",
                        height: 24,
                        fontSize: 12,
                        borderRadius: 6,
                        border:
                          "1px solid var(--joy-palette-neutral-outlinedBorder)",
                        background: isEditing
                          ? "var(--joy-palette-background-level1)"
                          : "var(--joy-palette-background-surface)",
                        color: "var(--joy-palette-text-primary)",
                      }}
                    />
                  </td>
                </tr>
              ))}

            {/* Other Profile Related */}
            <tr style={{ background: "var(--joy-palette-background-level1)" }}>
              <td style={{ padding: "8px 6px" }}>
                <IconButton
                  aria-label="expand row"
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => toggleRow("other_profile")}
                  sx={{ mr: 0.5 }}
                >
                  {expandedRows.has("other_profile") ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: 600, display: "inline" }}
                >
                  Other Profile Related
                </Typography>
              </td>
              <td></td>
            </tr>
            {expandedRows.has("other_profile") && null}

            {/* Asset Related */}
            <tr style={{ background: "var(--joy-palette-background-level1)" }}>
              <td style={{ padding: "8px 6px" }}>
                <IconButton
                  aria-label="expand row"
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => toggleRow("asset_related")}
                  sx={{ mr: 0.5 }}
                >
                  {expandedRows.has("asset_related") ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: 600, display: "inline" }}
                >
                  Asset Related
                </Typography>
              </td>
              <td></td>
            </tr>
            {expandedRows.has("asset_related") && null}

            {/* Services */}
            <tr style={{ background: "var(--joy-palette-background-level1)" }}>
              <td style={{ padding: "8px 6px" }}>
                <IconButton
                  aria-label="expand row"
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => toggleRow("services")}
                  sx={{ mr: 0.5 }}
                >
                  {expandedRows.has("services") ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
                <Typography
                  level="body-sm"
                  sx={{ fontWeight: 600, display: "inline" }}
                >
                  Services
                </Typography>
              </td>
              <td></td>
            </tr>
            {expandedRows.has("services") && null}
          </tbody>
        </Table>
      </Sheet>
    </Box>
  );
}
